import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Text,
  Title,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconClock } from "@tabler/icons-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Moment from "react-moment";
import { DateRanges, DateSelections, getDateRanges } from "utils/helper";

import { logger } from "utils/logger";
import classes from "./DateFilterPanel.module.scss";

// The stored range may be ISO strings (redux-persist / URL), but DatePicker
// only highlights Date values.
const toDates = (value = []) =>
  value.map((day) => (day ? moment(day).toDate() : null));

export const DateFilterPanel = ({
  onCancel,
  onApply,
  filter,
  filterItems,
  showTimePicker = true,
  mode="range",
  // When false the custom option keeps whatever range is picked rather than
  // filling in a week from the first day.
  weekOnly = true,
}) => {
  const [activeLink, setActiveLink] = useState(filter?.dateRange.item);
  const [date, setDate] = useState(toDates(filter?.dateRange.value));
  const timeRefFrom = useRef();
  const timeRefTo = useRef();
  const [timeRange, setTimeRange] = useState(filter?.timeRange);

  const form = useForm({
    initialValues: {
      ...filter,
    },
  });

  useEffect(() => {
    const range = getDateRanges(activeLink);
    if (activeLink.value !== DateRanges.CUSTOM) {
      setDate([range.startDate, range.endDate]);
    } else {
      setDate(toDates(filter.dateRange.value));
    }
  }, [activeLink]);

  const links = DateSelections.map((link, idx) => (
    <div key={idx}>
      <a
        className={`${classes.link} ${
          activeLink.value === link.value ? classes.linkActive : ""
        }`}
        href="/"
        onClick={(event) => {
          event.preventDefault();
          setActiveLink(link);
        }}
        key={link.value}
      >
        {link.label}
      </a>
    </div>
  ));

  const today = moment(new Date()).endOf("day").toDate();

  return (
    <Box shadow="xs" style={{ width: "100%" }}>
      <form
        onSubmit={form.onSubmit((values) => {
          var dateVals = [...date];
          if (!dateVals[1]) {
            dateVals[1] = new Date(dateVals[0]);
            dateVals[1].setHours(23, 59, 59, 999);
          }
          onApply({
            dateRange: { item: activeLink, value: dateVals },
            timeRange: timeRange,
          });
        })}
      >
        <Group align="start">
          {mode === 'range'?
          <Box className={classes.linkContainer}>{links}</Box>
          :null}
          
          <Box className={classes.pickerContainer}>
            <Title order={6}>{activeLink.label}</Title>
            <Text size={"xs"}>
              <Moment format="D MMM YYYY">{date[0]}</Moment> -{" "}
              {activeLink.value === DateRanges.CUSTOM && !date[1] ? (
                <Moment format="D MMM YYYY">{date[0]}</Moment>
              ) : (
                <Moment format="D MMM YYYY">{date[1]}</Moment>
              )}
            </Text>
            <Divider my={"xs"} />
            <Group justify="center" mt={"xs"}>
              <DatePicker
                // Open on the month the range starts in, not today's.
                // Remounts only when the start moves to another month.
                key={date[0] ? moment(date[0]).format("YYYY-MM") : "none"}
                defaultDate={date[0] ?? undefined}
                maxDate={today}
                type="range"
                // allowLevelChange={true}
                value={date}
                onChange={(range) => {
                  const val = range[0]
                  logger.log(val);
                  var timeout = 0;
                  const customLink = DateSelections.find(
                    (val) => val.value === DateRanges.CUSTOM
                  );
                  if (activeLink.value !== DateRanges.CUSTOM && customLink) {
                    setActiveLink(customLink);
                    timeout = 100;
                  }
                  setTimeout(() => {
                    // Unrestricted range: keep both picked days. The end
                    // stays empty until the second click (submit then uses
                    // the start day), and covers the whole of its day.
                    if (!weekOnly && mode === "range") {
                      var pickedEnd = range[1] ? new Date(range[1]) : null;
                      if (pickedEnd) {
                        pickedEnd.setHours(23, 59, 59, 999);
                        if (pickedEnd > today) pickedEnd = today;
                      }
                      setDate([val, pickedEnd]);
                      return;
                    }

                    // if (activeLink.value === DateRanges.CUSTOM) {
                    var startDate = val;
                    var endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + 7);
                    endDate.setHours(23, 59, 59, 999);

                    if (endDate > today) {
                      endDate = today;
                    }

                    if(mode !== 'range'){
                      endDate = startDate;
                      endDate.setHours(23, 59, 59, 999);
                    }

                    logger.log("end date:", endDate);
                    setDate([startDate, endDate]);
                  }, timeout);

                  // }
                }}
              />
              {/* <DatePicker
                                type='range'
                                numberOfColumns={1}
                                value={date}
                                onChange={(val) => {
                                    if (activeLink.value === DateRanges.CUSTOM) {
                                        setDate(val);
                                    }
                                }}
                                maxDate={new Date()}
                            /> */}
            </Group>

            {showTimePicker ? (
              <Group mt={"sm"}>
                <TimeInput
                  ref={timeRefFrom}
                  value={timeRange[0]}
                  onChange={(e) => {
                    logger.log(e);
                    setTimeRange([e.target.value, timeRange[1]]);
                  }}
                  rightSection={
                    <ActionIcon
                      onClick={() => timeRefFrom.current.showPicker()}
                    >
                      <IconClock size="12" stroke={1.5} />
                    </ActionIcon>
                  }
                />
                <Text>-</Text>
                <TimeInput
                  ref={timeRefTo}
                  value={timeRange[1]}
                  onChange={(e) => {
                    logger.log(e);
                    setTimeRange([timeRange[0], e.target.value]);
                  }}
                  rightSection={
                    <ActionIcon onClick={() => timeRefTo.current.showPicker()}>
                      <IconClock size="12" stroke={1.5} />
                    </ActionIcon>
                  }
                />
              </Group>
            ) : null}
          </Box>
        </Group>

        <Group justify="space-between" mt="lg" className={classes.actionBar}>
          <Button
            component="button"
            variant="subtle"
            size="sm"
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" variant="filled">
            Apply
          </Button>
        </Group>
      </form>
    </Box>
  );
};
